import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Form, InputGroup, Dropdown } from 'react-bootstrap';

const ItemsList = ({ 
  currentPath, 
  filesCurrentPath = [], 
  inheritance, 
  onItemClick, 
  onDownloadFile, 
  onFolderClick, 
  onBackClick 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('name-asc');
  
  // Get the current category from path
  const currentCategory = currentPath[currentPath.length - 1]?.toLowerCase();
  
  // Parse file paths into virtual folder structure (same logic as CategoryBrowser)
  const parseFilesIntoFolders = (filePaths, currentFolderPath = []) => {
    const folders = new Map();
    const files = [];
    
    filePaths.forEach(filePath => {
      const pathParts = filePath.split('/');
      
      // Check if file is in current folder context
      if (currentFolderPath.length === 0) {
        // At root level
        if (pathParts.length === 1) {
          // File is at root
          files.push(filePath);
        } else {
          // File is in a folder
          const folderName = pathParts[0];
          if (!folders.has(folderName)) {
            folders.set(folderName, {
              name: folderName,
              type: 'folder',
              fullPath: folderName,
              fileCount: 0
            });
          }
          folders.get(folderName).fileCount++;
        }
      } else {
        // In a subfolder
        const currentPathStr = currentFolderPath.join('/');
        if (filePath.startsWith(currentPathStr + '/')) {
          const remainingPath = filePath.substring(currentPathStr.length + 1);
          const remainingParts = remainingPath.split('/');
          
          if (remainingParts.length === 1) {
            // File is directly in current folder
            files.push(filePath);
          } else {
            // File is in a deeper subfolder
            const folderName = remainingParts[0];
            const folderFullPath = [...currentFolderPath, folderName].join('/');
            if (!folders.has(folderName)) {
              folders.set(folderName, {
                name: folderName,
                type: 'folder',
                fullPath: folderFullPath,
                fileCount: 0
              });
            }
            folders.get(folderName).fileCount++;
          }
        }
      }
    });
    
    return [...Array.from(folders.values()), ...files];
  };
  
  // Get items for the current category
  const getCategoryItems = () => {
    if (!inheritance || !inheritance.selectedItems) {
      return [];
    }
    
    switch (currentCategory) {
      case 'files':
        const allFiles = inheritance.selectedItems.files || [];
        // Parse into folder structure if we're in files category
        return parseFilesIntoFolders(allFiles, filesCurrentPath);
        
      case 'passwords':
        return inheritance.selectedItems.passwords || [];
      case 'banking':
        return inheritance.selectedItems.banking || [];
      case 'blockchain':
        return inheritance.selectedItems.blockchain || [];
      default:
        return [];
    }
  };
  
  // Helper to remove .txt extension
  const removeTxtExtension = (name) => {
    if (name && name.endsWith('.txt')) {
      return name.slice(0, -4);
    }
    return name;
  };
  
  // Get icon details based on item type
  const getIconDetails = (item, category) => {
    let iconClass, iconColor;
    
    // Handle both item objects and strings
    const itemName = typeof item === 'string' ? item : item.name;
    const itemType = typeof item === 'object' ? item.type : 'file';
    
    switch (category.toLowerCase()) {
      case 'files':
        // Check if it's a folder first
        if (itemType === 'folder') {
          iconClass = 'fa-folder';
          iconColor = '#FFD700';
        } else {
          // Determine file type based on extension
          const extension = itemName.split('.').pop().toLowerCase();
          
          if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
            iconClass = 'fa-file-image';
          } else if (['doc', 'docx', 'rtf', 'txt'].includes(extension)) {
            iconClass = 'fa-file-word';
          } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
            iconClass = 'fa-file-excel';
          } else if (['pdf'].includes(extension)) {
            iconClass = 'fa-file-pdf';
          } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
            iconClass = 'fa-file-video';
          } else if (['mp3', 'wav', 'ogg', 'flac', 'aac'].includes(extension)) {
            iconClass = 'fa-file-audio';
          } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
            iconClass = 'fa-file-archive';
          } else if (['html', 'css', 'js', 'php', 'py', 'java', 'c', 'cpp', 'cs', 'json'].includes(extension)) {
            iconClass = 'fa-file-code';
          } else {
            iconClass = 'fa-file-alt';
          }
          iconColor = '#000000';
        }
        break;
        
      case 'passwords':
        iconClass = 'fa-key';
        iconColor = '#FFD700';
        break;
        
      case 'banking':
        iconClass = 'fa-credit-card';
        iconColor = '#00CC5C';
        break;
        
      case 'blockchain':
        iconClass = 'fa-link';
        iconColor = '#4B0082';
        break;
        
      default:
        iconClass = 'fa-file';
        iconColor = '#000000';
    }
    
    return { iconClass, iconColor };
  };

  // Apply search and sort filters
  const getFilteredAndSortedItems = () => {
    let items = getCategoryItems();
    
    // Apply search filter
    if (searchTerm && searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      items = items.filter(item => {
        const itemName = typeof item === 'string' ? item : item.name;
        const displayName = removeTxtExtension(itemName);
        return displayName.toLowerCase().includes(lowerSearchTerm);
      });
    }

    // Apply sort
    switch (sortOption) {
      case 'name-asc':
        items.sort((a, b) => {
          // Always put folders first
          const aIsFolder = typeof a === 'object' && a.type === 'folder';
          const bIsFolder = typeof b === 'object' && b.type === 'folder';
          if (aIsFolder && !bIsFolder) return -1;
          if (!aIsFolder && bIsFolder) return 1;
          
          const nameA = removeTxtExtension(typeof a === 'string' ? a : a.name);
          const nameB = removeTxtExtension(typeof b === 'string' ? b : b.name);
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        });
        break;
      case 'name-desc':
        items.sort((a, b) => {
          // Always put folders first
          const aIsFolder = typeof a === 'object' && a.type === 'folder';
          const bIsFolder = typeof b === 'object' && b.type === 'folder';
          if (aIsFolder && !bIsFolder) return -1;
          if (!aIsFolder && bIsFolder) return 1;
          
          const nameA = removeTxtExtension(typeof a === 'string' ? a : a.name);
          const nameB = removeTxtExtension(typeof b === 'string' ? b : b.name);
          return nameB.toLowerCase().localeCompare(nameA.toLowerCase());
        });
        break;
      default:
        // Default to name ascending
        items.sort((a, b) => {
          // Always put folders first
          const aIsFolder = typeof a === 'object' && a.type === 'folder';
          const bIsFolder = typeof b === 'object' && b.type === 'folder';
          if (aIsFolder && !bIsFolder) return -1;
          if (!aIsFolder && bIsFolder) return 1;
          
          const nameA = removeTxtExtension(typeof a === 'string' ? a : a.name);
          const nameB = removeTxtExtension(typeof b === 'string' ? b : b.name);
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        });
    }

    return items;
  };

  const items = getFilteredAndSortedItems();
  
  // Handle item click (files and folders)
  const handleItemClick = (item) => {
    if (typeof item === 'object' && item.type === 'folder') {
      // Navigate into folder
      onFolderClick(item.name);
    } else {
      // View file - pass the full path
      const filePath = typeof item === 'string' ? item : item.name;
      onItemClick(filePath, currentCategory);
    }
  };
  
  // Handle download (only for files)
  const handleDownload = (item) => {
    const filePath = typeof item === 'string' ? item : item.name;
    onDownloadFile(filePath);
  };
  
  // If no items in this category
  if (items.length === 0) {
    const hasSearchTerm = searchTerm && searchTerm.trim();
    return (
      <div className="re-items-container">
        <div className="re-controls-header mb-4">
  <div className="re-controls-row">
    <div className="re-search-container">
      <InputGroup>
        <Form.Control
          type="text"
          placeholder="Search items..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <InputGroup.Text>
          <i className="fas fa-search"></i>
        </InputGroup.Text>
      </InputGroup>
    </div>

    <Dropdown className="re-sort-dropdown">
      <Dropdown.Toggle variant="outline-secondary" size="sm">
        <i className="fas fa-sort me-1"></i>
        {sortOption === 'name-asc' && 'A-Z'}
        {sortOption === 'name-desc' && 'Z-A'}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => setSortOption('name-asc')}>
          <i className="fas fa-sort-alpha-down me-2"></i>Name (A-Z)
        </Dropdown.Item>
        <Dropdown.Item onClick={() => setSortOption('name-desc')}>
          <i className="fas fa-sort-alpha-down-alt me-2"></i>Name (Z-A)
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  </div>
</div>

        <div className="re-no-items-message">
          <i className="fas fa-search re-no-items-icon"></i>
          <h5>No Items Found</h5>
          <p>
            {hasSearchTerm 
              ? `No items matching "${searchTerm}" in this location.`
              : `There are no items in this location.`
            }
          </p>
          {hasSearchTerm && (
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={() => setSearchTerm('')}
            >
              Clear Search
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="re-items-container">
      <div className="re-controls-header mb-4">
  <div className="re-controls-row">
    <div className="re-search-container">
      <InputGroup>
        <Form.Control
          type="text"
          placeholder="Search items..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <InputGroup.Text>
          <i className="fas fa-search"></i>
        </InputGroup.Text>
      </InputGroup>
    </div>

    <Dropdown className="re-sort-dropdown">
      <Dropdown.Toggle variant="outline-secondary" size="sm">
        <i className="fas fa-sort me-1"></i>
        {sortOption === 'name-asc' && 'A-Z'}
        {sortOption === 'name-desc' && 'Z-A'}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => setSortOption('name-asc')}>
          <i className="fas fa-sort-alpha-down me-2"></i>Name (A-Z)
        </Dropdown.Item>
        <Dropdown.Item onClick={() => setSortOption('name-desc')}>
          <i className="fas fa-sort-alpha-down-alt me-2"></i>Name (Z-A)
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
    
    {/* Optional: Show item count in a subtle way */}
    <div className="re-item-count">
      <small className="text-muted">{items.length} items</small>
    </div>
  </div>
</div>
      
      <div className="re-items-list">
        {items.map((item, index) => {
          const { iconClass, iconColor } = getIconDetails(item, currentCategory);
          const itemName = typeof item === 'string' ? item : item.name;
          const isFolder = typeof item === 'object' && item.type === 'folder';
          const displayName = isFolder ? item.name : removeTxtExtension(itemName.split('/').pop());
          
          return (
            <div 
              key={index} 
              className="re-item"
              data-item-name={itemName}
              data-item-type={currentCategory}
              onClick={() => handleItemClick(item)}
              style={{ cursor: 'pointer' }}
            >
              <div className="re-item-content">
                <div className="re-item-icon-wrapper">
                  <i className={`fas ${iconClass}`} style={{ color: iconColor }}></i>
                </div>
                <div className="re-item-name" title={displayName}>
                  {displayName}
                  {isFolder && (
                    <small className="text-muted d-block" style={{ fontSize: '0.8em' }}>
                      {item.fileCount} item{item.fileCount !== 1 ? 's' : ''}
                    </small>
                  )}
                </div>
              </div>
              
              <div className="re-item-actions">
                {!isFolder && currentCategory === 'files' && (
                  <Button 
                    variant="link"
                    size="sm"
                    className="re-btn re-btn-download p-1" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(item);
                    }}
                    title="Download File"
                  >
                    <i className="fas fa-download" style={{ color: '#FF0000' }}></i>
                  </Button>
                )}
                
                {!isFolder && (
                  <Button 
                    variant="link"
                    size="sm"
                    className="re-btn re-btn-view p-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(item);
                    }}
                    title="View Item"
                  >
                    <i className="fas fa-eye" style={{ color: '#00CC5C' }}></i>
                  </Button>
                )}
                
                {isFolder && (
                  <Button 
                    variant="link"
                    size="sm"
                    className="re-btn re-btn-view p-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(item);
                    }}
                    title="Open Folder"
                  >
                    <i className="fas fa-folder-open" style={{ color: '#00CC5C' }}></i>
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

ItemsList.propTypes = {
  currentPath: PropTypes.array.isRequired,
  filesCurrentPath: PropTypes.array,
  inheritance: PropTypes.object,
  onItemClick: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  onFolderClick: PropTypes.func,
  onBackClick: PropTypes.func
};

ItemsList.defaultProps = {
  filesCurrentPath: [],
  onFolderClick: () => {},
  onBackClick: () => {}
};

export default ItemsList;